import * as React from 'react'
import { useMemo, useState } from 'react'

import { inject, observer } from 'mobx-react'

import useUploadLogsFilter from '~/client/src/desktop/hooks/useUploadLogsFilter'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DeliveryDetailsStore from '~/client/src/shared/components/DeliveryDetails/DeliveryDetails.store'
import useUploadLogFacets from '~/client/src/shared/hooks/useUploadLogFacets'
import useUploadLogs from '~/client/src/shared/hooks/useUploadLogs'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import UploadLogConfigurationStore from '~/client/src/shared/stores/domain/UploadLogConfiguration.store'
import UploadLogsStore from '~/client/src/shared/stores/domain/UploadLogs.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowsStore from '~/client/src/shared/stores/domain/Workflows.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import AttachmentsComponent from './AttachmentsComponent'

import './Attachments.scss'

interface IProps {
  eventsStore?: DesktopEventStore
  uploadLogsStore?: UploadLogsStore
  uploadLogConfigurationStore?: UploadLogConfigurationStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  userProjectsStore?: UserProjectsStore
  projectsStore?: ProjectsStore
  projectDateStore?: ProjectDateStore
  deliveriesStore?: DeliveriesStore
  workflowsStore?: WorkflowsStore
  state?: DesktopInitialState
  locationAttributesStore?: LocationAttributesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
  deliveryDetailsStore?: DeliveryDetailsStore
  workflowTypesStore?: WorkflowTypesStore
}

const AttachmentsWrapper: React.FC<IProps> = props => {
  const [datesFilter, setDatesFilter] = useState<{
    startDate?: number
    endDate?: number
  }>({
    startDate: undefined,
    endDate: undefined,
  })
  const { eventsStore, locationAttributesStore, workflowTypesStore } = props

  const filters = useUploadLogsFilter(eventsStore.appState, workflowTypesStore)

  const combinedFilters = useMemo(
    () => ({
      ...datesFilter,
      ...filters,
    }),
    [datesFilter, filters],
  )

  const { uploadLogs, uploadLogsLoading, workflows } = useUploadLogs(
    eventsStore.appState.activeProject.id,
    combinedFilters,
  )

  const { facetsData: viewableFacets, facetsLoading: viewableFacetsLoading } =
    useUploadLogFacets(eventsStore.appState.activeProject.id, combinedFilters, {
      locationAttributesStore,
    })

  const { facetsData: totalFacets, facetsLoading: totalFacetsLoading } =
    useUploadLogFacets(
      eventsStore.appState.activeProject.id,
      {},
      { locationAttributesStore },
    )

  const facetsLoading = viewableFacetsLoading || totalFacetsLoading

  return (
    <AttachmentsComponent
      {...props}
      allWorkflows={workflows}
      uploadLogsLoading={uploadLogsLoading}
      facetsLoading={facetsLoading}
      viewableFacets={viewableFacets}
      totalFacets={totalFacets}
      uploadLogs={uploadLogs}
      setDatesFilter={setDatesFilter}
      totalFacetsLoading={totalFacetsLoading}
    />
  )
}

const AttachmentsWrapperComponent = inject(
  'eventsStore',
  'uploadLogsStore',
  'uploadLogConfigurationStore',
  'companiesStore',
  'projectMembersStore',
  'userProjectsStore',
  'projectsStore',
  'projectDateStore',
  'deliveriesStore',
  'workflowsStore',
  'state',
  'locationAttributesStore',
  'workflowCategoriesStore',
  'deliveryDetailsStore',
  'workflowTypesStore',
)(observer(AttachmentsWrapper))

export default AttachmentsWrapperComponent
